/**
 * ConnectInstructor — pupil-side unified entry point for joining an
 * instructor. Three clear options. After submit, the page polls the
 * pupil's request inbox so status changes appear in real time.
 *
 * Owner directive (May 5 2026): "no pupil request should disappear"
 * — every status, including SMS-invited and admin-routed, must be
 * visible here.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, MapPin, ArrowRight, Loader2, ChevronLeft, Sparkles,
  Clock, CheckCircle2, AlertCircle, X, Repeat,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  pending: { label: "Sending invite", tone: "amber", Icon: Clock },
  sent_to_instructor: { label: "Waiting for instructor", tone: "indigo", Icon: Clock },
  open_network: { label: "Finding instructors near you", tone: "indigo", Icon: MapPin },
  accepted: { label: "Accepted", tone: "emerald", Icon: CheckCircle2 },
  declined: { label: "Declined", tone: "rose", Icon: X },
  expired: { label: "Expired", tone: "slate", Icon: AlertCircle },
};

const TONE = {
  amber: "bg-amber-500/15 border border-amber-500/30 text-amber-200",
  indigo: "bg-indigo-500/15 border border-indigo-500/30 text-indigo-200",
  emerald: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-200",
  rose: "bg-rose-500/15 border border-rose-500/30 text-rose-200",
  slate: "bg-slate-700/40 border border-slate-700 text-slate-300",
};

const TYPE_LABEL = {
  known_instructor: "Instructor by phone",
  network_search: "Help finding an instructor",
  transfer: "Transferring instructor",
  invite_accept: "Invited",
};

function StatusPill({ status }) {
  const meta = STATUS[status] || STATUS.pending;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={`conn-status-${status}`}
      className={`inline-flex items-center gap-1.5 text-[11px] font-extrabold rounded-full px-2.5 py-1 ${TONE[meta.tone]}`}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function ConnectInstructor() {
  const [mode, setMode] = useState(null);  // 'known' | 'network' | 'transfer'
  const [submitting, setSubmitting] = useState(false);

  // known
  const [phone, setPhone] = useState("");
  // network + transfer
  const [postcode, setPostcode] = useState("");
  const [locationText, setLocationText] = useState("");
  // transfer-only
  const [transferNotes, setTransferNotes] = useState("");
  const [previousInstructor, setPreviousInstructor] = useState("");
  const [urgency, setUrgency] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try {
      const r = await axios.get(
        `${API}/api/connection-requests/my-requests`,
        { withCredentials: true }
      );
      setItems(r.data?.items || []);
    } catch {
      // silent — keep prior state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const submit = async () => {
    let body;
    if (mode === "known") {
      if (!phone.trim()) return toast.error("Enter a phone number first");
      body = { target_type: "known_instructor", target_phone: phone.trim() };
    } else if (mode === "network") {
      if (!postcode.trim()) return toast.error("Enter a postcode");
      body = {
        target_type: "network_search",
        postcode: postcode.trim(),
        location_text: locationText.trim() || null,
      };
    } else if (mode === "transfer") {
      if (!postcode.trim()) return toast.error("Enter a postcode");
      body = {
        target_type: "transfer",
        postcode: postcode.trim(),
        location_text: locationText.trim() || null,
        transfer_notes: transferNotes.trim() || null,
        previous_instructor_name: previousInstructor.trim() || null,
        urgency: urgency || null,
      };
    } else {
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/connection-requests/create`, body, {
        withCredentials: true,
      });
      toast.success("Request sent");
      setMode(null);
      setPhone(""); setPostcode(""); setLocationText("");
      setTransferNotes(""); setPreviousInstructor(""); setUrgency("");
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white">
      <header className="px-4 py-4 border-b border-slate-800 flex items-center gap-3">
        <Link to="/learn" className="p-2 rounded-lg hover:bg-white/5">
          <ChevronLeft className="w-5 h-5 text-slate-400" />
        </Link>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
            Connect with an instructor
          </p>
          <h1 className="text-lg font-extrabold">Find your instructor</h1>
        </div>
      </header>

      <div className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {!mode && (
          <div className="space-y-3">
            <button
              data-testid="conn-mode-known"
              onClick={() => setMode("known")}
              className="w-full rounded-2xl bg-indigo-600 hover:bg-indigo-500 transition active:scale-[0.99] p-4 flex items-start gap-3 text-left"
            >
              <Phone className="w-5 h-5 text-white flex-none mt-0.5" />
              <div className="flex-1">
                <p className="text-base font-extrabold text-white">
                  I have my instructor's phone number
                </p>
                <p className="text-[11px] text-indigo-100/80 mt-0.5">
                  We'll connect you straight away if they're on the platform — or send them an SMS invite.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-white flex-none mt-1" />
            </button>

            <button
              data-testid="conn-mode-network"
              onClick={() => setMode("network")}
              className="w-full rounded-2xl bg-slate-800 hover:bg-slate-700 transition active:scale-[0.99] p-4 flex items-start gap-3 text-left border border-slate-700"
            >
              <MapPin className="w-5 h-5 text-indigo-300 flex-none mt-0.5" />
              <div className="flex-1">
                <p className="text-base font-extrabold text-white">
                  Help me find an instructor
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  We'll match you with ADIs in your area.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 flex-none mt-1" />
            </button>

            <button
              data-testid="conn-mode-transfer"
              onClick={() => setMode("transfer")}
              className="w-full rounded-2xl bg-slate-800 hover:bg-slate-700 transition active:scale-[0.99] p-4 flex items-start gap-3 text-left border border-slate-700"
            >
              <Repeat className="w-5 h-5 text-amber-300 flex-none mt-0.5" />
              <div className="flex-1">
                <p className="text-base font-extrabold text-white">
                  I'm transferring from another instructor
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Add your progress notes — instructors near you will see it.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 flex-none mt-1" />
            </button>
          </div>
        )}

        {mode && (
          <div
            data-testid="conn-form"
            className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <button
                data-testid="conn-form-back"
                onClick={() => setMode(null)}
                className="p-1.5 rounded hover:bg-white/5"
              >
                <ChevronLeft className="w-4 h-4 text-slate-400" />
              </button>
              <p className="text-[11px] uppercase tracking-widest font-extrabold text-indigo-300">
                {mode === "known" ? "Instructor's phone" :
                 mode === "network" ? "Find near you" :
                 "Transfer details"}
              </p>
            </div>
            {mode === "known" && (
              <div>
                <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                  Mobile number
                </label>
                <input
                  data-testid="conn-input-phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="07700 900 123"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder-slate-500"
                />
              </div>
            )}
            {(mode === "network" || mode === "transfer") && (
              <>
                <div>
                  <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                    Postcode
                  </label>
                  <input
                    data-testid="conn-input-postcode"
                    type="text"
                    placeholder="LS1 4AB"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                    Area / lessons preferred (optional)
                  </label>
                  <input
                    data-testid="conn-input-location-text"
                    type="text"
                    placeholder="Leeds North · weekday evenings"
                    value={locationText}
                    onChange={(e) => setLocationText(e.target.value)}
                    className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder-slate-500"
                  />
                </div>
              </>
            )}
            {mode === "transfer" && (
              <>
                <div>
                  <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                    Previous instructor (optional)
                  </label>
                  <input
                    data-testid="conn-input-prev-instructor"
                    type="text"
                    placeholder="Name or school"
                    value={previousInstructor}
                    onChange={(e) => setPreviousInstructor(e.target.value)}
                    className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                    Progress notes / what you've covered
                  </label>
                  <textarea
                    data-testid="conn-input-transfer-notes"
                    placeholder="Junctions OK, working on roundabouts…"
                    value={transferNotes}
                    onChange={(e) => setTransferNotes(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1">
                    Urgency
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {["low", "medium", "high"].map((u) => (
                      <button
                        key={u}
                        data-testid={`conn-urgency-${u}`}
                        onClick={() => setUrgency(u)}
                        className={`h-10 rounded-lg text-[12px] font-extrabold capitalize ring-2 ${
                          urgency === u
                            ? "ring-indigo-500 bg-indigo-500/15 text-white"
                            : "ring-transparent bg-slate-800 text-slate-300 hover:bg-slate-700"
                        }`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <button
              data-testid="conn-form-submit"
              onClick={submit}
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-extrabold text-sm flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
              ) : (
                <>Send request <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        )}

        <div>
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500 mb-2 mt-3 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-indigo-300" />
            Your requests
          </p>
          {loading ? (
            <p className="text-xs text-slate-400">Loading…</p>
          ) : items.length === 0 ? (
            <p
              data-testid="conn-no-requests"
              className="text-xs text-slate-500"
            >
              No requests yet — pick an option above to get started.
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`conn-request-${it.id}`}
                  className="rounded-xl bg-slate-900 border border-slate-800 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-extrabold text-white">
                        {TYPE_LABEL[it.target_type] || it.target_type}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {it.target_phone || it.postcode || ""}
                      </p>
                      {it.transfer_notes && (
                        <p className="text-[11px] text-slate-300 mt-1 line-clamp-2">
                          “{it.transfer_notes}”
                        </p>
                      )}
                    </div>
                    <StatusPill status={it.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
